/**
 * Series renderers + the registry they live in.
 *
 * The chart core never names a series type. It looks one up by string, asks it
 * for an extent, hands it a plot context and asks it to draw. That indirection
 * is the point: `candlestick` and `volume` become `registerSeriesType(...)`
 * calls, not edits to the chart.
 *
 * Mark spec enforced here (it is not decoration — these are the house rules):
 *   - 2px strokes
 *   - >= 8px markers
 *   - 4px rounded data-ends, anchored square to the baseline
 *   - 2px surface gap between adjacent / stacked fills
 *   - 2px surface ring on overlapping marks
 */

import { AXIS_FONT_PX } from "./axes";
import { withAlpha } from "./charts-color.mjs";
import { endTagBox } from "./charts-endtag.mjs";
import { applySurfaceGap } from "./charts-stack.mjs";
import { roundedRect } from "./layers";
import { candlesSeries } from "./candles-series";
import type { ResolvedPoint, SeriesHit, SeriesPlotContext, SeriesRenderer } from "./types";

export const LINE_WIDTH = 2;
export const MARKER_MIN_SIZE = 8;
export const DATA_END_RADIUS = 4;
export const SURFACE_GAP = 2;

const registry = new Map<string, SeriesRenderer>();

/**
 * Register a series type. Trading shapes (candlestick, volume, trade markers)
 * arrive this way; nothing in the chart core changes.
 */
export function registerSeriesType(renderer: SeriesRenderer) {
  registry.set(renderer.type, renderer);
}

export function getSeriesType(type: string): SeriesRenderer {
  const r = registry.get(type);
  if (!r) {
    throw new Error(
      `charts: unknown series type "${type}" (registered: ${[...registry.keys()].join(", ")})`
    );
  }
  return r;
}

export function hasSeriesType(type: string): boolean {
  return registry.has(type);
}

/* ── shared helpers ───────────────────────────────────────────────────────── */

function pointExtent(points: ReadonlyArray<ResolvedPoint>) {
  let min = Infinity;
  let max = -Infinity;
  for (const p of points) {
    if (p.y == null || !Number.isFinite(p.y)) continue;
    if (p.y < min) min = p.y;
    if (p.y > max) max = p.y;
  }
  return min === Infinity ? null : { min, max };
}

/** Screen position of a point along the position axis. */
function posOf(p: ResolvedPoint, i: number, pc: SeriesPlotContext): number {
  if (pc.band) return pc.band.center(i);
  return pc.posAt(p.x);
}

/** Value pixel for a point, honouring the stack when there is one. */
function valuePixels(
  p: ResolvedPoint,
  i: number,
  pc: SeriesPlotContext
): { lo: number; hi: number } | null {
  if (pc.stack) {
    const b = pc.stack[i];
    if (!b || b.gap) return null;
    return { lo: pc.valueAt(b.y0), hi: pc.valueAt(b.y1) };
  }
  if (p.y == null || !Number.isFinite(p.y)) return null;
  return { lo: pc.baseline, hi: pc.valueAt(p.y) };
}

/** Nearest point along the position axis; used by the shared crosshair. */
function nearestByPos(
  points: ReadonlyArray<ResolvedPoint>,
  px: number,
  pc: SeriesPlotContext
): SeriesHit | null {
  let best: SeriesHit | null = null;
  let bestD = Infinity;
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    if (p.y == null) continue;
    const x = posOf(p, i, pc);
    const d = Math.abs(x - px);
    if (d < bestD) {
      bestD = d;
      const vp = valuePixels(p, i, pc);
      best = { index: i, px: x, py: vp ? vp.hi : pc.baseline, value: p.y };
    }
  }
  return best;
}

/** Path along the series, breaking at gaps so a null is visibly a hole. */
function tracePath(
  points: ReadonlyArray<ResolvedPoint>,
  pc: SeriesPlotContext,
  useStackTop: boolean
): Array<Array<{ x: number; y: number }>> {
  const runs: Array<Array<{ x: number; y: number }>> = [];
  let run: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const vp = valuePixels(p, i, pc);
    if (!vp || (!useStackTop && p.y == null)) {
      if (run.length) runs.push(run);
      run = [];
      continue;
    }
    run.push({ x: posOf(p, i, pc), y: useStackTop ? vp.hi : pc.valueAt(p.y as number) });
  }
  if (run.length) runs.push(run);
  return runs;
}

function strokeRuns(
  g: CanvasRenderingContext2D,
  runs: Array<Array<{ x: number; y: number }>>,
  color: string,
  dash = false
) {
  g.lineWidth = LINE_WIDTH;
  g.lineJoin = "round";
  g.lineCap = "round";
  g.strokeStyle = color;
  // Set on the context, cleared at the end: a dash left behind leaks into the
  // next series drawn on this canvas.
  if (dash) g.setLineDash([5, 4]);
  for (const run of runs) {
    if (run.length === 1) {
      // A lone point still has to be visible; draw it as a marker-sized dot.
      g.beginPath();
      g.arc(run[0].x, run[0].y, MARKER_MIN_SIZE / 2, 0, Math.PI * 2);
      g.fillStyle = color;
      g.fill();
      continue;
    }
    g.beginPath();
    g.moveTo(run[0].x, run[0].y);
    for (let i = 1; i < run.length; i++) g.lineTo(run[i].x, run[i].y);
    g.stroke();
  }
  g.setLineDash([]);
}

/** Hover ring on the point under the crosshair: 2px of surface, then the hue. */
function drawHoverMark(g: CanvasRenderingContext2D, x: number, y: number, pc: SeriesPlotContext) {
  const r = MARKER_MIN_SIZE / 2;
  g.beginPath();
  g.arc(x, y, r, 0, Math.PI * 2);
  g.fillStyle = pc.color;
  g.fill();
  g.lineWidth = SURFACE_GAP;
  g.strokeStyle = pc.theme.surface;
  g.stroke();
}

/* ── line ─────────────────────────────────────────────────────────────────── */

export const lineSeries: SeriesRenderer = {
  type: "line",
  xKind: "linear",
  stackable: false,
  hover: "shared",
  extent: pointExtent,
  draw(points, pc) {
    const g = pc.ctx2d;
    const runs = tracePath(points, pc, false);
    if (pc.fill === "gradient") {
      // A flat wash (what `area` uses) would read as a second area series
      // competing with the line; a fade to nothing reads as emphasis on it.
      const grd = g.createLinearGradient(0, pc.plot.y, 0, pc.plot.y + pc.plot.height);
      grd.addColorStop(0, withAlpha(pc.color, 0.22));
      grd.addColorStop(1, withAlpha(pc.color, 0));
      for (const run of runs) {
        if (run.length < 2) continue;
        g.beginPath();
        g.moveTo(run[0].x, run[0].y);
        for (let i = 1; i < run.length; i++) g.lineTo(run[i].x, run[i].y);
        g.lineTo(run[run.length - 1].x, pc.baseline);
        g.lineTo(run[0].x, pc.baseline);
        g.closePath();
        g.fillStyle = grd;
        g.fill();
      }
    }
    strokeRuns(g, runs, pc.color, pc.dash);
    if (pc.endTag) {
      // Walk back from the end: the final point can be a gap, and a tag on a
      // gap would label a value that is not there.
      let last = -1;
      for (let i = points.length - 1; i >= 0; i--) {
        if (points[i].y != null) {
          last = i;
          break;
        }
      }
      if (last >= 0) {
        const px = posOf(points[last], last, pc);
        const py = pc.valueAt(points[last].y as number);
        const text = pc.format(points[last].y as number);
        g.save();
        // `theme.fontMono` is a font-family LIST, not a CSS font shorthand.
        // Assigning it bare is invalid and canvas ignores it silently, so the
        // tag used to measure and draw in whatever font was left over — which
        // is why its width was wrong and it landed off the plot. Every other
        // font assignment in this library builds the shorthand; so does this.
        g.font = `${AXIS_FONT_PX}px ${pc.theme.fontMono}`;
        const w = g.measureText(text).width + 10;
        const h = 16;
        const box = endTagBox({ px, py, w, h, gap: 6, plot: pc.plot });
        g.beginPath();
        g.arc(px, py, 2.5, 0, Math.PI * 2);
        g.fillStyle = pc.color;
        g.fill();
        g.fillRect(box.x, box.y, w, h);
        g.fillStyle = pc.theme.onAccent;
        g.textAlign = "left";
        g.textBaseline = "middle";
        g.fillText(text, box.x + 5, box.y + h / 2);
        g.restore();
      }
    }
    if (pc.hoverIndex != null) {
      const p = points[pc.hoverIndex];
      if (p && p.y != null) {
        drawHoverMark(g, posOf(p, pc.hoverIndex, pc), pc.valueAt(p.y), pc);
      }
    }
  },
  hitTest(points, px, _py, pc) {
    return nearestByPos(points, px, pc);
  },
};

/* ── area (incl. stacked) ─────────────────────────────────────────────────── */

export const areaSeries: SeriesRenderer = {
  type: "area",
  xKind: "linear",
  stackable: true,
  hover: "shared",
  extent(points) {
    const e = pointExtent(points);
    // An area is read against its baseline, so zero belongs in the domain.
    return e ? { min: Math.min(0, e.min), max: Math.max(0, e.max) } : null;
  },
  draw(points, pc) {
    const g = pc.ctx2d;
    const runs: Array<Array<{ x: number; top: number; bottom: number }>> = [];
    let run: Array<{ x: number; top: number; bottom: number }> = [];
    for (let i = 0; i < points.length; i++) {
      const vp = valuePixels(points[i], i, pc);
      if (!vp) {
        if (run.length) runs.push(run);
        run = [];
        continue;
      }
      // 2px of surface between this band and the one stacked below it.
      const gapped = pc.stack
        ? applySurfaceGap(vp.lo, vp.hi, {
            gap: SURFACE_GAP,
            before: pc.groupIndex > 0,
            after: pc.groupIndex < pc.groupCount - 1,
          })
        : { a: Math.min(vp.lo, vp.hi), b: Math.max(vp.lo, vp.hi) };
      const top = vp.hi <= vp.lo ? gapped.a : gapped.b;
      const bottom = vp.hi <= vp.lo ? gapped.b : gapped.a;
      run.push({ x: posOf(points[i], i, pc), top, bottom });
    }
    if (run.length) runs.push(run);

    for (const r of runs) {
      if (r.length < 2) continue;
      g.beginPath();
      g.moveTo(r[0].x, r[0].top);
      for (let i = 1; i < r.length; i++) g.lineTo(r[i].x, r[i].top);
      for (let i = r.length - 1; i >= 0; i--) g.lineTo(r[i].x, r[i].bottom);
      g.closePath();
      // Fill is recessive so the 2px edge stays the thing you read.
      g.fillStyle = withAlpha(pc.color, pc.stack ? 0.7 : 0.22);
      g.fill();
    }
    strokeRuns(
      g,
      runs.map((r) => r.map((p) => ({ x: p.x, y: p.top }))),
      pc.color
    );
    if (pc.hoverIndex != null) {
      const vp = valuePixels(points[pc.hoverIndex], pc.hoverIndex, pc);
      if (vp) drawHoverMark(g, posOf(points[pc.hoverIndex], pc.hoverIndex, pc), vp.hi, pc);
    }
  },
  hitTest(points, px, _py, pc) {
    return nearestByPos(points, px, pc);
  },
};

/* ── bar (vertical + horizontal, grouped + stacked) ───────────────────────── */

/** Geometry of one bar, in the chart's own orientation. */
function barRect(
  points: ReadonlyArray<ResolvedPoint>,
  i: number,
  pc: SeriesPlotContext
): { x: number; y: number; w: number; h: number; positive: boolean } | null {
  const vp = valuePixels(points[i], i, pc);
  if (!vp) return null;
  const band = pc.band;
  const slotStart = band ? band.start(i) : posOf(points[i], i, pc) - 8;
  const slotWidth = band ? band.bandWidth : 16;
  // Grouped bars split the band; a 2px surface gap keeps neighbours apart.
  const grouped = pc.stack ? 1 : pc.groupCount;
  const slot = slotWidth / grouped;
  const idx = pc.stack ? 0 : pc.groupIndex;
  const gap = grouped > 1 ? SURFACE_GAP : 0;
  const w = Math.max(1, slot - gap);
  const along = slotStart + idx * slot + gap / 2;

  let a = Math.min(vp.lo, vp.hi);
  let b = Math.max(vp.lo, vp.hi);
  if (pc.stack) {
    const g2 = applySurfaceGap(a, b, {
      gap: SURFACE_GAP,
      before: pc.groupIndex > 0,
      after: pc.groupIndex < pc.groupCount - 1,
    });
    a = g2.a;
    b = g2.b;
  }
  const positive = pc.stack
    ? (pc.stack[i]?.value ?? 0) >= 0
    : (points[i].y ?? 0) >= 0;

  if (pc.orientation === "horizontal") {
    return { x: a, y: along, w: Math.max(1, b - a), h: w, positive };
  }
  return { x: along, y: a, w, h: Math.max(1, b - a), positive };
}

export const barSeries: SeriesRenderer = {
  type: "bar",
  xKind: "band",
  stackable: true,
  hover: "mark",
  extent(points) {
    const e = pointExtent(points);
    // Bars measure length from zero. An axis that starts elsewhere lies.
    return e ? { min: Math.min(0, e.min), max: Math.max(0, e.max) } : null;
  },
  draw(points, pc) {
    const g = pc.ctx2d;
    g.fillStyle = pc.color;
    for (let i = 0; i < points.length; i++) {
      const r = barRect(points, i, pc);
      if (!r) continue;
      // 4px rounded data-end, square where it meets the baseline.
      const rr = DATA_END_RADIUS;
      const radii: [number, number, number, number] =
        pc.orientation === "horizontal"
          ? r.positive
            ? [0, rr, rr, 0]
            : [rr, 0, 0, rr]
          : r.positive
            ? [rr, rr, 0, 0]
            : [0, 0, rr, rr];
      roundedRect(g, r.x, r.y, r.w, r.h, radii);
      g.fillStyle = pc.hoverIndex === i ? withAlpha(pc.color, 0.85) : pc.color;
      g.fill();
      if (pc.hoverIndex === i) {
        // 2px surface ring so the hovered bar reads above its neighbours.
        g.lineWidth = SURFACE_GAP;
        g.strokeStyle = pc.theme.text;
        g.stroke();
      }
    }
  },
  hitTest(points, px, py, pc) {
    // Hit target is the whole band, not the drawn bar: the spec asks for
    // targets larger than the mark, and a 4px-tall bar is unhittable otherwise.
    const along = pc.orientation === "horizontal" ? py : px;
    const i = pc.band ? pc.band.indexAt(along) : -1;
    if (i < 0 || i >= points.length) return null;
    const r = barRect(points, i, pc);
    if (!r) return null;
    return {
      index: i,
      px: pc.orientation === "horizontal" ? r.x + r.w : r.x + r.w / 2,
      py: pc.orientation === "horizontal" ? r.y + r.h / 2 : r.y,
      value: points[i].y,
    };
  },
};

/* ── scatter / dot ────────────────────────────────────────────────────────── */

export const scatterSeries: SeriesRenderer = {
  type: "scatter",
  xKind: "linear",
  stackable: false,
  hover: "mark",
  extent: pointExtent,
  draw(points, pc) {
    const g = pc.ctx2d;
    const r = MARKER_MIN_SIZE / 2;
    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      if (p.y == null) continue;
      const x = posOf(p, i, pc);
      const y = pc.valueAt(p.y);
      g.beginPath();
      g.arc(x, y, pc.hoverIndex === i ? r + 2 : r, 0, Math.PI * 2);
      g.fillStyle = pc.color;
      g.fill();
      // 2px surface ring so overlapping dots stay countable.
      g.lineWidth = SURFACE_GAP;
      g.strokeStyle = pc.hoverIndex === i ? pc.theme.text : pc.theme.surface;
      g.stroke();
    }
  },
  hitTest(points, px, py, pc) {
    // Generous radius — the hit target is bigger than the mark by design.
    const reach = MARKER_MIN_SIZE * 1.5;
    let best: SeriesHit | null = null;
    let bestD = reach * reach;
    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      if (p.y == null) continue;
      const x = posOf(p, i, pc);
      const y = pc.valueAt(p.y);
      const d = (x - px) * (x - px) + (y - py) * (y - py);
      if (d <= bestD) {
        bestD = d;
        best = { index: i, px: x, py: y, value: p.y };
      }
    }
    return best;
  },
};

/* ── sparkline ────────────────────────────────────────────────────────────── */

/**
 * A sparkline is a line without furniture — no axes, no legend, no hover. It is
 * the one shape allowed to omit the hover layer, alongside a bare stat tile.
 */
export const sparklineSeries: SeriesRenderer = {
  type: "sparkline",
  xKind: "linear",
  stackable: false,
  hover: "none",
  extent: pointExtent,
  draw(points, pc) {
    const g = pc.ctx2d;
    const runs = tracePath(points, pc, false);
    g.lineWidth = LINE_WIDTH;
    g.lineJoin = "round";
    g.lineCap = "round";
    g.strokeStyle = pc.color;
    for (const run of runs) {
      if (run.length < 2) continue;
      g.beginPath();
      g.moveTo(run[0].x, run[0].y);
      for (let i = 1; i < run.length; i++) g.lineTo(run[i].x, run[i].y);
      g.stroke();
    }
    // The last value is the one a sparkline is read for — anchor it with a dot.
    const last = runs[runs.length - 1];
    if (last && last.length) {
      const p = last[last.length - 1];
      g.beginPath();
      g.arc(p.x, p.y, MARKER_MIN_SIZE / 2 - 1, 0, Math.PI * 2);
      g.fillStyle = pc.color;
      g.fill();
    }
  },
  hitTest() {
    return null;
  },
};

registerSeriesType(lineSeries);
registerSeriesType(areaSeries);
registerSeriesType(barSeries);
registerSeriesType(scatterSeries);
registerSeriesType(sparklineSeries);
// Domain-shaped renderers arrive through the same seam:
registerSeriesType(candlesSeries);
